import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Input, Table } from 'antd'
import { useSelector } from 'react-redux'
import { API } from 'src/utils/interceptor'
import Loading from 'src/components/styled/Loading'
import styled from 'styled-components/macro'
import {
  DEFAULT_SUPPLIERS_GUIDANCE,
  DEFAULT_SUPPLIERS_NO_SEARCH_RESULTS,
  DEFAULT_SUPPLIERS_SEARCH_PLACEHOLDER,
} from 'src/utils/supplierInvitesAdminCopy'
import {
  DEFAULT_SUPPLIERS_EMPTY_STATE_COPY,
  buildDefaultSuppliersTableColumns,
  prepareDefaultSuppliersTableRows,
} from 'src/utils/defaultSuppliersTable'
import { showErrorToast } from 'src/utils/messageToast'

const AdminDefaultSuppliers = () => {
  const username = useSelector(state => state.auth.cognito.username)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    if (username) {
      fetchDefaultSuppliers()
    }
  }, [username])

  const fetchDefaultSuppliers = async () => {
    if (!username) {
      showErrorToast('Username not available')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const response = await API.get('api2', 'suppliers/default-suppliers', {
        queryStringParameters: { username },
      })

      if (response.success) {
        setRows(response.data || [])
      } else {
        showErrorToast(response.message || 'Failed to load default suppliers')
      }
    } catch (error) {
      console.error('Error fetching default suppliers:', error)
      showErrorToast('Failed to load default suppliers')
    } finally {
      setLoading(false)
    }
  }

  const columns = useMemo(() => buildDefaultSuppliersTableColumns(), [])
  const tableRows = useMemo(
    () => prepareDefaultSuppliersTableRows(rows, searchTerm),
    [rows, searchTerm],
  )
  const hasSearch = Boolean(String(searchTerm || '').trim())
  const showEmptyState = !loading && tableRows.length === 0

  if (loading) {
    return (
      <TabContent>
        <Loading />
      </TabContent>
    )
  }

  return (
    <TabContent>
      <GuidanceAlert
        type='info'
        showIcon
        message={DEFAULT_SUPPLIERS_GUIDANCE}
      />
      <SearchInput
        allowClear
        value={searchTerm}
        placeholder={DEFAULT_SUPPLIERS_SEARCH_PLACEHOLDER}
        onChange={event => setSearchTerm(event.target.value)}
      />
      {showEmptyState ? (
        <EmptyState>
          <EmptyStateText>
            {hasSearch
              ? DEFAULT_SUPPLIERS_NO_SEARCH_RESULTS
              : DEFAULT_SUPPLIERS_EMPTY_STATE_COPY}
          </EmptyStateText>
        </EmptyState>
      ) : (
        <Table
          dataSource={tableRows}
          columns={columns}
          pagination={false}
          scroll={{ x: true }}
        />
      )}
    </TabContent>
  )
}

const TabContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const GuidanceAlert = styled(Alert)`
  margin-bottom: 0;
`

const SearchInput = styled(Input)`
  max-width: 360px;
`

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  background-color: #fafafa;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
`

const EmptyStateText = styled.p`
  font-size: 14px;
  color: #8c8c8c;
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
`

export default AdminDefaultSuppliers
